import AttachFileRounded from '@mui/icons-material/AttachFileRounded';
import DeleteOutlineRounded from '@mui/icons-material/DeleteOutlineRounded';
import DescriptionRounded from '@mui/icons-material/DescriptionRounded';
import DownloadRounded from '@mui/icons-material/DownloadRounded';
import EditRounded from '@mui/icons-material/EditRounded';
import ImageRounded from '@mui/icons-material/ImageRounded';
import PhotoCameraRounded from '@mui/icons-material/PhotoCameraRounded';
import PictureAsPdfRounded from '@mui/icons-material/PictureAsPdfRounded';
import TableChartRounded from '@mui/icons-material/TableChartRounded';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  Stack,
  TextField,
  Tooltip,
  Typography,
  type SxProps,
  type Theme,
} from '@mui/material';
import { useRef, useState, type DragEvent, type ReactNode } from 'react';
import { ApiError } from '../../api/client';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { dateTime } from '../../lib/format';
import { useNotify } from '../../lib/notify';
import { useShop } from '../../shop/ShopProvider';
import { fileSize } from './size';
import {
  downloadAttachment,
  useAttachmentActions,
  useAttachmentFile,
  useAttachments,
  type Attachment,
  type RecordType,
} from './api';

/** What the file pickers offer (the server checks the bytes anyway). */
const ACCEPT = [
  '.pdf',
  'image/*',
  '.heic',
  '.doc',
  '.docx',
  '.xls',
  '.xlsx',
  '.ppt',
  '.pptx',
  '.odt',
  '.ods',
  '.csv',
  '.txt',
].join(',');

const isImage = (f: Attachment) => /^image\/(jpeg|png|webp|gif)$/.test(f.mimeType);
const isPdf = (f: Attachment) => f.mimeType === 'application/pdf';
/** The desktop app blocks frames, so PDFs are saved there instead of shown. */
const canShowPdf = () => typeof window !== 'undefined' && !('packpilot' in window);

function FileIcon({ file }: { file: Attachment }) {
  if (isPdf(file)) return <PictureAsPdfRounded sx={{ color: 'error.main' }} />;
  if (file.mimeType.startsWith('image/')) return <ImageRounded sx={{ color: 'info.main' }} />;
  if (/sheet|excel|csv/.test(file.mimeType))
    return <TableChartRounded sx={{ color: 'success.main' }} />;
  return <DescriptionRounded sx={{ color: 'primary.main' }} />;
}

function Thumb({ file }: { file: Attachment }) {
  const src = useAttachmentFile(file, isImage(file));
  return (
    <Box
      sx={{
        width: 44,
        height: 44,
        flexShrink: 0,
        borderRadius: 1.5,
        bgcolor: 'action.hover',
        display: 'grid',
        placeItems: 'center',
        overflow: 'hidden',
      }}
    >
      {isImage(file) && src.data ? (
        <Box
          component="img"
          src={src.data}
          alt=""
          sx={{ width: 1, height: 1, objectFit: 'cover' }}
        />
      ) : (
        <FileIcon file={file} />
      )}
    </Box>
  );
}

/**
 * Files attached to a record (scanned bills, slips, contracts, photos): drop or pick files, take a
 * photo on a phone, preview, download, describe and remove. Hidden from users who may not see the
 * record's files; read-only for users who may see but not change them.
 */
export function AttachmentsPanel({
  recordType,
  recordId,
  sx,
}: {
  recordType: RecordType;
  recordId: string;
  sx?: SxProps<Theme>;
}) {
  const notify = useNotify();
  const { format } = useShop();
  const list = useAttachments(recordType, recordId);
  const actions = useAttachmentActions(recordType, recordId);
  const pick = useRef<HTMLInputElement>(null);
  const camera = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const [busy, setBusy] = useState(0);
  const [preview, setPreview] = useState<Attachment | null>(null);
  const [editing, setEditing] = useState<Attachment | null>(null);
  const [removing, setRemoving] = useState<Attachment | null>(null);

  const error = list.error;
  if (error instanceof ApiError && error.code === 'OFFLINE')
    return (
      <Panel sx={sx} count={0}>
        <Typography variant="body2" color="text.secondary">
          Attachments are available when the app is online.
        </Typography>
      </Panel>
    );
  if (error) return null; // no permission to see this record's files
  if (!list.data) return null;
  const { data: files, canAdd, maxBytes } = list.data;
  if (!canAdd && files.length === 0) return null;

  const upload = async (picked: FileList | File[] | null) => {
    const all = [...(picked ?? [])];
    if (all.length === 0) return;
    const tooBig = all.filter((f) => f.size > maxBytes);
    for (const f of tooBig)
      notify.error(
        new ApiError(413, 'FILE_TOO_LARGE', `${f.name} is larger than ${fileSize(maxBytes)}.`),
      );
    const ok = all.filter((f) => f.size <= maxBytes);
    setBusy(ok.length);
    let done = 0;
    for (const file of ok) {
      try {
        await actions.upload.mutateAsync({ file });
        done += 1;
      } catch (e) {
        notify.error(e);
      }
      setBusy((n) => n - 1);
    }
    if (done) notify.success(done === 1 ? 'File attached' : `${String(done)} files attached`);
  };

  const onDrop = (e: DragEvent) => {
    e.preventDefault();
    setDragging(false);
    if (canAdd) void upload(e.dataTransfer.files);
  };
  const open = (f: Attachment) => {
    if (isImage(f) || (isPdf(f) && canShowPdf())) setPreview(f);
    else downloadAttachment(f).catch(notify.error);
  };

  return (
    <Panel
      sx={sx}
      count={files.length}
      dragging={dragging}
      onDragOver={
        canAdd
          ? (e) => {
              e.preventDefault();
              setDragging(true);
            }
          : undefined
      }
      onDragLeave={() => setDragging(false)}
      onDrop={onDrop}
      actions={
        canAdd && (
          <Stack direction="row" spacing={1}>
            <Button
              size="small"
              startIcon={<PhotoCameraRounded />}
              onClick={() => camera.current?.click()}
              sx={{ display: { md: 'none' } }}
            >
              Photo
            </Button>
            <Button
              size="small"
              variant="outlined"
              startIcon={<AttachFileRounded />}
              onClick={() => pick.current?.click()}
              disabled={busy > 0}
            >
              Add files
            </Button>
          </Stack>
        )
      }
    >
      <input
        ref={pick}
        type="file"
        hidden
        multiple
        accept={ACCEPT}
        data-testid="attachment-input"
        onChange={(e) => {
          void upload(e.target.files);
          e.target.value = '';
        }}
      />
      <input
        ref={camera}
        type="file"
        hidden
        accept="image/*"
        capture="environment"
        onChange={(e) => {
          void upload(e.target.files);
          e.target.value = '';
        }}
      />
      {busy > 0 && <LinearProgress sx={{ mb: 1.5 }} aria-label="Uploading" />}
      {files.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          Drop files here or use Add files: scanned bills, slips, contracts, photos (PDF, images,
          Word, Excel; up to {fileSize(maxBytes)} each).
        </Typography>
      ) : (
        <Stack spacing={1.25}>
          {files.map((f) => (
            <Stack key={f.id} direction="row" spacing={1.25} sx={{ alignItems: 'center' }}>
              <Thumb file={f} />
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography
                  component="button"
                  type="button"
                  onClick={() => open(f)}
                  variant="body2"
                  sx={{
                    all: 'unset',
                    cursor: 'pointer',
                    fontWeight: 600,
                    display: 'block',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    '&:hover, &:focus-visible': { textDecoration: 'underline' },
                  }}
                >
                  {f.fileName}
                </Typography>
                {f.description && (
                  <Typography variant="body2" color="text.secondary" noWrap>
                    {f.description}
                  </Typography>
                )}
                <Typography variant="caption" color="text.secondary">
                  {fileSize(f.sizeBytes)} · {f.uploadedBy.name} · {dateTime(f.createdAt, format)}
                </Typography>
              </Box>
              <Stack direction="row" sx={{ flexShrink: 0 }}>
                <Tooltip title="Download">
                  <IconButton
                    size="small"
                    aria-label={`Download ${f.fileName}`}
                    onClick={() => downloadAttachment(f).catch(notify.error)}
                  >
                    <DownloadRounded fontSize="small" />
                  </IconButton>
                </Tooltip>
                {canAdd && (
                  <>
                    <Tooltip title="Description">
                      <IconButton
                        size="small"
                        aria-label={`Describe ${f.fileName}`}
                        onClick={() => setEditing(f)}
                      >
                        <EditRounded fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Remove">
                      <IconButton
                        size="small"
                        aria-label={`Remove ${f.fileName}`}
                        onClick={() => setRemoving(f)}
                      >
                        <DeleteOutlineRounded fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </>
                )}
              </Stack>
            </Stack>
          ))}
        </Stack>
      )}

      {preview && <PreviewDialog file={preview} onClose={() => setPreview(null)} />}
      {editing && (
        <DescribeDialog
          file={editing}
          onClose={() => setEditing(null)}
          onSave={(description) =>
            actions.describe
              .mutateAsync({ file: editing, description })
              .then(() => setEditing(null), notify.error)
          }
        />
      )}
      <ConfirmDialog
        open={!!removing}
        title="Remove this file?"
        body={`${removing?.fileName ?? ''} is taken off this record. The activity log keeps a note of it.`}
        confirmLabel="Remove"
        danger
        onConfirm={() =>
          removing
            ? actions.remove.mutateAsync(removing).then(() => notify.success('File removed'))
            : Promise.resolve()
        }
        onClose={() => setRemoving(null)}
      />
    </Panel>
  );
}

function Panel(props: {
  sx?: SxProps<Theme> | undefined;
  count: number;
  actions?: ReactNode;
  dragging?: boolean;
  onDragOver?: ((e: DragEvent) => void) | undefined;
  onDragLeave?: () => void;
  onDrop?: (e: DragEvent) => void;
  children: ReactNode;
}) {
  return (
    <Card
      sx={props.sx}
      onDragOver={props.onDragOver}
      onDragLeave={props.onDragLeave}
      onDrop={props.onDrop}
      aria-label="Attachments"
      component="section"
    >
      <CardContent
        sx={{
          p: { xs: 2.5, sm: 3 },
          outline: props.dragging ? '2px dashed' : 'none',
          outlineColor: 'primary.main',
          outlineOffset: -4,
        }}
      >
        <Stack
          direction="row"
          spacing={1}
          sx={{ alignItems: 'center', mb: 1.5, justifyContent: 'space-between', flexWrap: 'wrap' }}
        >
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
            <AttachFileRounded fontSize="small" color="action" />
            <Typography variant="h6" sx={{ fontSize: '1rem' }}>
              Attachments
            </Typography>
            {props.count > 0 && <Chip size="small" label={props.count} />}
          </Stack>
          {props.actions}
        </Stack>
        {props.children}
      </CardContent>
    </Card>
  );
}

function PreviewDialog({ file, onClose }: { file: Attachment; onClose: () => void }) {
  const notify = useNotify();
  const src = useAttachmentFile(file);
  return (
    <Dialog open onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ overflowWrap: 'anywhere' }}>{file.fileName}</DialogTitle>
      <DialogContent sx={{ display: 'grid', placeItems: 'center', minHeight: 200 }}>
        {src.isError ? (
          <Alert severity="error">The file could not be loaded.</Alert>
        ) : !src.data ? (
          <CircularProgress />
        ) : isPdf(file) ? (
          <Box
            component="iframe"
            src={src.data}
            title={file.fileName}
            sx={{ width: 1, height: '75vh', border: 0 }}
          />
        ) : (
          <Box
            component="img"
            src={src.data}
            alt={file.description ?? file.fileName}
            sx={{ maxWidth: 1, maxHeight: '75vh', objectFit: 'contain' }}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button
          startIcon={<DownloadRounded />}
          onClick={() => downloadAttachment(file).catch(notify.error)}
        >
          Download
        </Button>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

function DescribeDialog({
  file,
  onClose,
  onSave,
}: {
  file: Attachment;
  onClose: () => void;
  onSave: (description: string) => Promise<unknown>;
}) {
  const [text, setText] = useState(file.description ?? '');
  const [saving, setSaving] = useState(false);
  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Describe the file</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          fullWidth
          label="Description"
          value={text}
          onChange={(e) => setText(e.target.value)}
          helperText={file.fileName}
          slotProps={{ htmlInput: { maxLength: 500 } }}
          sx={{ mt: 1 }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={saving}
          onClick={() => {
            setSaving(true);
            void onSave(text).finally(() => setSaving(false));
          }}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
